import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';

function fmtDateBR(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatPeriod(c) {
  if (!c) return '-';
  const s = fmtDateBR(c.periodStart);
  const e = fmtDateBR(c.periodEnd);
  if (c.periodType === 'DAILY') return s;
  return `${s} a ${e}`;
}

const KPI_DEFS = [
  { key: 'leads', label: 'Leads', icon: 'person_add', color: 'text-blue-500', bg: 'bg-blue-50' },
  { key: 'appointments', label: 'Agendamentos', icon: 'event', color: 'text-purple-500', bg: 'bg-purple-50' },
  { key: 'attendances', label: 'Comparecimentos', icon: 'how_to_reg', color: 'text-teal-500', bg: 'bg-teal-50' },
  { key: 'closings', label: 'Fechamentos', icon: 'task_alt', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  { key: 'conversationsStarted', label: 'Conversas Iniciadas', icon: 'chat', color: 'text-amber-500', bg: 'bg-amber-50' },
  { key: 'metaAdsLeadsCount', label: 'Leads Meta Ads', icon: 'hub', color: 'text-blue-500', bg: 'bg-blue-50' },
  { key: 'landingPageLeadsCount', label: 'Leads Landing Page', icon: 'language', color: 'text-slate-500', bg: 'bg-slate-100' },
];

// Reused both for the live "Relatórios" page (with action bar) and the public
// /shared/:token viewer (readOnly, no action bar) — same house style as the other
// snapshot report viewers in SharedReport.jsx (KPI card row + detail table).
export default function ClinicRoutineReportVisualization({
  routineName, adAccountName, pageName, periodStart, periodEnd,
  summary, collections = [], onBack, onShare, readOnly = false,
}) {
  const reportRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const ratioLeadToAppt = summary?.leads > 0 ? ((summary.appointments / summary.leads) * 100).toFixed(1) : null;
  const ratioApptToAttend = summary?.appointments > 0 ? ((summary.attendances / summary.appointments) * 100).toFixed(1) : null;

  const handleExportPng = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const link = document.createElement('a');
      link.download = `relatorio_${(routineName || 'clinica').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {!readOnly && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button onClick={onBack} className="px-6 py-2 rounded-full border border-outline-variant text-sm font-semibold hover:bg-white transition-all text-on-surface flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">arrow_back</span> Voltar
          </button>
          <div className="flex items-center gap-2">
            <button onClick={handleExportPng} disabled={exporting} className="primary-pill-gradient text-white px-6 py-2 rounded-full text-sm font-bold hover:opacity-90 transition-all shadow-sm flex items-center gap-1 disabled:opacity-60">
              <span className="material-symbols-outlined text-sm">{exporting ? 'progress_activity' : 'image'}</span> {exporting ? 'Exportando...' : 'Exportar PNG'}
            </button>
            <button onClick={onShare} className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 px-6 py-2 rounded-full text-sm font-bold transition-all shadow-sm flex items-center gap-1">
              <span className="material-symbols-outlined text-sm">share</span> Compartilhar
            </button>
          </div>
        </div>
      )}

      <div ref={reportRef} className="bg-white p-6 lg:p-8 rounded-2xl border border-slate-100 shadow-sm space-y-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold text-primary uppercase tracking-widest mb-1">Relatório da Unidade</p>
            <h2 className="font-headline font-extrabold text-3xl text-on-surface">{routineName || 'Relatório'}</h2>
            <div className="flex items-center gap-3 mt-2 text-sm text-slate-500 flex-wrap">
              {adAccountName && <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[16px]">hub</span>{adAccountName}</span>}
              {pageName && <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[16px]">facebook</span>{pageName}</span>}
            </div>
          </div>
          {periodStart && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-bold shrink-0">
              <span className="material-symbols-outlined text-[16px]">calendar_month</span>
              {fmtDateBR(periodStart)} — {fmtDateBR(periodEnd)}
            </span>
          )}
        </div>

        {/* KPIs */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
            {KPI_DEFS.map(kpi => {
              const raw = summary[kpi.key];
              const value = raw === null || raw === undefined ? '—' : Number(raw).toLocaleString('pt-BR');
              return (
                <div key={kpi.key} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center">
                  <div className={`w-12 h-12 rounded-full ${kpi.bg} flex items-center justify-center mb-3`}>
                    <span className={`material-symbols-outlined ${kpi.color} text-2xl`}>{kpi.icon}</span>
                  </div>
                  <h4 className="text-2xl font-black font-headline text-slate-800 tracking-tight leading-none mb-1">{value}</h4>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{kpi.label}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Ratios */}
        {(ratioLeadToAppt || ratioApptToAttend) && (
          <div className="grid grid-cols-2 gap-4">
            {ratioLeadToAppt && (
              <div className="bg-slate-800 rounded-xl px-5 py-4 text-white">
                <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">Lead → Agendamento</p>
                <p className="text-2xl font-bold mt-1">{ratioLeadToAppt}%</p>
              </div>
            )}
            {ratioApptToAttend && (
              <div className="bg-slate-800 rounded-xl px-5 py-4 text-white">
                <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">Agendamento → Comparecimento</p>
                <p className="text-2xl font-bold mt-1">{ratioApptToAttend}%</p>
              </div>
            )}
          </div>
        )}

        {/* Per-period table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h3 className="font-headline font-bold text-lg text-slate-800">Detalhamento por Período</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50/50">
                <tr>
                  {['Período', 'Leads', 'Agend.', 'Compare.', 'Fechamentos', 'Conversas Iniciadas', 'Leads Meta Ads', 'Leads Landing Page'].map(h => (
                    <th key={h} className="py-3 px-5 text-[10px] font-bold text-slate-500 uppercase tracking-widest whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {collections.map(c => (
                  <tr key={c.id} className="hover:bg-slate-50/80">
                    <td className="py-3 px-5 font-bold text-slate-700 whitespace-nowrap">{formatPeriod(c)}</td>
                    <td className="py-3 px-5 text-slate-700">{c.isFilled ? (c.entry?.leads ?? '-') : <span className="text-slate-300">—</span>}</td>
                    <td className="py-3 px-5 text-slate-700">{c.isFilled ? (c.entry?.appointments ?? '-') : <span className="text-slate-300">—</span>}</td>
                    <td className="py-3 px-5 text-slate-700">{c.isFilled ? (c.entry?.attendances ?? '-') : <span className="text-slate-300">—</span>}</td>
                    <td className="py-3 px-5 text-slate-700">{c.isFilled ? (c.entry?.closings ?? '-') : <span className="text-slate-300">—</span>}</td>
                    <td className="py-3 px-5 text-teal-700 font-bold">{c.conversationsStarted === null || c.conversationsStarted === undefined ? '—' : c.conversationsStarted}</td>
                    <td className="py-3 px-5 text-slate-700">{c.metaAdsLeadsCount === null || c.metaAdsLeadsCount === undefined ? '—' : c.metaAdsLeadsCount}</td>
                    <td className="py-3 px-5 text-slate-700">{c.landingPageLeadsCount === null || c.landingPageLeadsCount === undefined ? '—' : c.landingPageLeadsCount}</td>
                  </tr>
                ))}
                {collections.length === 0 && (
                  <tr><td colSpan={8} className="py-16 text-center text-slate-400 text-sm">Nenhum período no intervalo selecionado.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
